import { useEffect, useState } from "react"
import styles from "./WorkApplyModal.module.scss"
import { useFormik } from "formik"
import * as Yup from "yup"
import { connect } from "react-redux"
import {
  closeMainModal,
  openMainModal,
  getAllWorkBagUsers,
  sendIdentityPhotos,
  openIdentityModal,
} from "../../store/actions"
import { useNavigate } from "react-router-dom"
import PhotoUploadModal from "../../../src/components/FileUpload/PhotosUploadModal"
import { motion } from "framer-motion"
import { X } from "lucide-react"
import ApplyExternalJobOffer from "./ApplyExternalJobOffer"
import ModalOverlay from "../../components/UI/ModalOverlay/ModalOverlay.tsx"

const variants = {
  visible: {
    scale: 1.0,
    opacity: 1,
  },
  hidden: {
    scale: 1.1,
    opacity: 0,
  },
}


const WorkApplyModal = ({
  workBagUsers,
  setShowApplyModal,
  openMainModal,
  closeMainModal,
  getAllWorkBagUsers,
  workOffer,
  sendIdentityPhotos,
  forbiddenClose,
  setShowForm,
  skipWorkBagUserCheck = false,
  prefilledEmail,
  identityValidated: identityValidatedProp,
}) => {
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false)
  const [showApplyExternalJobOffer, setShowApplyExternalJobOffer] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    if (!skipWorkBagUserCheck) {
      getAllWorkBagUsers()
    }
  }, [getAllWorkBagUsers, skipWorkBagUserCheck])

  const isExternalOffer = !workOffer || workOffer?.user?.username.toLowerCase() === "gonzalo@edify.la";

  const handlePhotoModalState = () => {
    setShowPhotoUploadModal(true)
  }

  const handleSendPhotos = (files) => {
    const email = formik.values.email
    sendIdentityPhotos(files, email, navigate)
  }

  const handleSubmit = (values) => {
    const emailToUse = values.email.trim().toLowerCase()
    const isUserInWorkBag = skipWorkBagUserCheck
      ? null
      : workBagUsers.find((user) => (user.email ?? "").toLowerCase() === emailToUse)
    const identityValidated = skipWorkBagUserCheck
      ? Boolean(identityValidatedProp)
      : Boolean(isUserInWorkBag?.identity_validated)

    if (!skipWorkBagUserCheck && !isUserInWorkBag) {
      setShowApplyModal(false)
      openMainModal({
        title: "¡Ups!",
        body: "No encontramos tu email en nuestra base de datos. Para postularte a esta oferta, debes inscribirte en la bolsa de trabajo.",
        isSuccess: true,
        iconType: "warning",
        noLabel: "Inscribirme",
        customCloseMainModal: () => {
          navigate(`/bolsadetrabajo`)
          closeMainModal()
          setShowForm()
        },
      })
    } else if (isExternalOffer) {
        setShowApplyExternalJobOffer(true)
        closeMainModal()

    } else {
      if (!identityValidated) {
      openMainModal({
        title: "Validación de identidad requerida",
        iconType: "scanFace",
        body: "Para postularte, debes validar tu identidad. Es muy fácil, solo necesitas subir unas fotos de tu DNI.",
        yesLabel: "Subir fotos",
        confirmHandler: () => {
          closeMainModal()
          handlePhotoModalState()
        },
      })
      } else {
        setShowApplyModal(false)
        closeMainModal()
        const targetPath = skipWorkBagUserCheck ? `/bolsa-trabajo/${workOffer.id}` : `/bolsadetrabajo/${workOffer.id}`
        navigate(targetPath)
      }
    }
  }

  const initialValues = {
    email: (prefilledEmail ?? "").toLowerCase(),
  }

  const formSchema = {
    email: Yup.string().email("Email inválido").required("Campo requerido"),
  }

  const formik = useFormik({
    initialValues,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema: Yup.object(formSchema),
    onSubmit: (values) => {
      handleSubmit(values)
    },
  })

  const handleClosePhotoUploadModal = () => {
    setShowPhotoUploadModal(false)
  }

  const handleClose = () => {
    setShowApplyModal(false)
  }


  return (
    <>
      <ModalOverlay isOpen={true} onClose={handleClose}>
        <motion.div
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={variants}
          className={styles.modal}
          onClick={(e) => e.stopPropagation()}
        >
          <header className={styles.header}>
            <h2 className={styles.header_title}>¿Te interesa esta oportunidad?</h2>
            { isExternalOffer && !forbiddenClose && (
            <button className={styles.header_button} onClick={handleClose}>
              <X size={20} />
            </button>
            )}
          </header>
          <main className={styles.main}>
            <h4>{prefilledEmail ? "Vamos a usar tu correo para postularte" : "Ingresa tu correo electrónico para postularte"}</h4>
            <form
              className={`${styles.form} ${prefilledEmail ? styles.formPrefilled : ""}`}
              onSubmit={formik.handleSubmit}
            >
              {skipWorkBagUserCheck && prefilledEmail ? (
                <div className={styles.prefilledEmail}>
                  <span className={styles.prefilledEmail_label}>Correo asociado a tu cuenta</span>
                  <span className={styles.prefilledEmail_value}>{prefilledEmail}</span>
                </div>
              ) : (
                <input
                  className={styles.form_input}
                  type="email"
                  placeholder="ejemplo@mail.com"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                />
              )}
              <button type="submit" className={styles.form_button} disabled={!formik.isValid}>
                {prefilledEmail ? "Continuar" : "Enviar"}
              </button>
            </form>
          </main>
        </motion.div>
      </ModalOverlay>
      {showPhotoUploadModal && (
        <PhotoUploadModal
          closeModal={handleClosePhotoUploadModal}
          handleSendPhotos={handleSendPhotos}
          onClick={() => {
            setShowPhotoUploadModal(false)
            openMainModal({
              title: "¡Excelente!",
              body: "Tus fotos han sido enviadas para verificación. Te notificaremos cuando tu identidad sea aprobada.",
              isSuccess: true,
              iconType: "success",
              customCloseMainModal: () => {
                closeMainModal()
              },
            })
          }}
        />
      )}
        {showApplyExternalJobOffer && (
            <ApplyExternalJobOffer
            workOffer={workOffer}
            isOpen={showApplyExternalJobOffer}
            onClose={() => setShowApplyExternalJobOffer(false)}
            />
        )}
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    workBagUsers: state.register.workBagUsers,
  }
}

export default connect(mapStateToProps, {
  openMainModal,
  closeMainModal,
  getAllWorkBagUsers,
  sendIdentityPhotos,
  openIdentityModal,
})(WorkApplyModal)
